import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, X, ChevronRight, ShieldCheck, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { createEvent, getEventById, listEventsByOwner, updateEvent } from "@/lib/entitiesStore";
import { canCreateMore } from "@/lib/monetizationStore";
import { fileToImageDataUrl } from "@/lib/hooks";
import CategoryPicker from "@/components/CategoryPicker";
import { EVENT_CATEGORIES } from "@/lib/eventCategories";
import { generateEventDescription } from "@/lib/aiMock";

const CreateEvent = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { id: editId } = useParams();
    const isEdit = Boolean(editId);
    const existing = useMemo(() => (isEdit ? getEventById(editId) : null), [editId, isEdit]);
    const fileRef = useRef(null);
    const [title, setTitle] = useState(existing?.title || "");
    const [cat, setCat] = useState({
        category: existing?.categoryGroup || (existing?.customCategory ? "" : existing?.category) || "",
        subcategory: existing?.subcategory || "",
        customLabel: existing?.customCategory || null,
    });
    const [pickerOpen, setPickerOpen] = useState(false);
    const [date, setDate] = useState(existing?.date || "");
    const [time, setTime] = useState(existing?.time || "");
    const [location, setLocation] = useState(existing?.location || "Lagos, Nigeria");
    const [description, setDescription] = useState(existing?.description || "");
    const [isFree, setIsFree] = useState(existing ? !!existing.isFree : true);
    const [price, setPrice] = useState(existing?.price ? String(existing.price) : "");
    const [refundPolicy, setRefundPolicy] = useState(existing?.refundPolicy || "refundable");
    const [images, setImages] = useState(() => {
        if (!existing) return [];
        if (Array.isArray(existing.images)) return existing.images.slice(0, 5);
        if (existing.image) return [existing.image];
        return [];
    });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const [limitMsg, setLimitMsg] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);

    // If we land on /edit/event/:id but the event doesn't exist (deleted, wrong id),
    // bail back to the listing rather than showing an empty form.
    useEffect(() => {
        if (isEdit && !existing) navigate("/manage", { replace: true });
    }, [isEdit, existing, navigate]);

    // Owner-only guard.
    useEffect(() => {
        if (isEdit && existing && user && existing.ownerId && existing.ownerId !== user.id) {
            navigate(`/events/${existing.id}`, { replace: true });
        }
    }, [isEdit, existing, user, navigate]);

    const handleAIGenerate = async () => {
        setAiBusy(true);
        await new Promise((r) => setTimeout(r, 700));
        const text = generateEventDescription({
            title,
            category: cat.customLabel || cat.category,
            subcategory: cat.subcategory,
            location,
            isFree,
            price: Number(price) || 0,
            date,
        });
        setDescription(text);
        if (errors.description) setErrors({ ...errors, description: null });
        setAiBusy(false);
    };

    const today = new Date().toISOString().slice(0, 10);

    const categoryLabel = cat.customLabel
        ? cat.customLabel
        : cat.category && cat.subcategory
        ? `${cat.category} · ${cat.subcategory}`
        : cat.category || "";

    const handleImages = async (files) => {
        if (!files?.length) return;
        const remaining = 5 - images.length;
        const picked = Array.from(files).slice(0, remaining);
        setBusy(true);
        try {
            const urls = [];
            for (const f of picked) {
                if (!f.type.startsWith("image/")) continue;
                urls.push(await fileToImageDataUrl(f, 1200, 0.78));
            }
            setImages((prev) => [...prev, ...urls]);
        } finally {
            setBusy(false);
        }
    };

    const removeImage = (idx) =>
        setImages((prev) => prev.filter((_, i) => i !== idx));

    const handleSubmit = () => {
        // Free user listing limit (only for creating new — not editing).
        if (!isEdit) {
            const currentCount = user ? listEventsByOwner(user.id).length : 0;
            if (!canCreateMore(user, "events", currentCount)) {
                setLimitMsg("You've reached your free limit. Upgrade to create unlimited listings.");
                return;
            }
        }

        const e = {};
        if (!title.trim()) e.title = "Event name is required";
        const hasCat = !!(
            cat.customLabel ||
            (cat.category && (cat.subcategory || cat.category === "Other"))
        );
        if (!hasCat) e.category = "Choose a category";
        if (!date) e.date = "Pick a date";
        else if (!isEdit && date < today) e.date = "Date can't be in the past";
        if (!time) e.time = "Pick a time";
        if (!isFree && (!price || Number(price) <= 0))
            e.price = "Enter a valid price";
        if (images.length < 1) e.images = "Add at least 1 image";
        if (!description.trim() || description.trim().length < 10)
            e.description = "At least 10 characters";

        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }

        const finalCategory = cat.customLabel
            ? cat.customLabel
            : cat.subcategory || cat.category;

        const payload = {
            title,
            category: finalCategory,
            categoryGroup: cat.category || null,
            subcategory: cat.subcategory || null,
            customCategory: cat.customLabel || null,
            date,
            time,
            location,
            description,
            isFree,
            price,
            refundPolicy: isFree ? null : refundPolicy,
            images,
            image: images[0] || null,
        };

        if (isEdit) {
            updateEvent(editId, { ...payload, updatedAt: Date.now() });
            navigate(`/events/${editId}`, { replace: true });
        } else {
            const ev = createEvent(user, payload);
            navigate(`/events/${ev.id}`, { replace: true });
        }
    };

    return (
        <div className="screen relative" data-testid="create-event-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="create-event-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">{isEdit ? "Edit Event" : "Create Event"}</h1>
                <span className="h-10 w-10" />
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-5">
                {/* Images */}
                <section>
                    <label className="block text-[12px] font-medium text-muted-foreground mb-2 ml-1">
                        Photos ({images.length}/5)
                    </label>
                    <div className="grid grid-cols-3 gap-2" data-testid="create-event-images">
                        {images.map((src, i) => (
                            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border" data-testid={`create-event-image-${i}`}>
                                <img src={src} alt="" className="h-full w-full object-cover" />
                                <button
                                    onClick={() => removeImage(i)}
                                    data-testid={`create-event-image-remove-${i}`}
                                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border flex items-center justify-center text-destructive"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                        {images.length < 5 && (
                            <button
                                data-testid="create-event-image-add"
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                                className="aspect-square rounded-xl border-2 border-dashed border-border bg-secondary flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-60"
                            >
                                <ImagePlus size={20} />
                                <span className="text-[11px] font-medium">Add</span>
                            </button>
                        )}
                        <input
                            ref={fileRef}
                            data-testid="create-event-image-input"
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleImages(e.target.files)}
                        />
                    </div>
                    {errors.images && (
                        <p data-testid="create-event-images-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.images}
                        </p>
                    )}
                </section>

                <Field label="Event name" error={errors.title} testId="create-event-title">
                    <input
                        data-testid="create-event-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Afrobeats Night"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                {/* Category & Subcategory selector — opens 2-step picker */}
                <div data-testid="create-event-category-field">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Category
                    </span>
                    <button
                        type="button"
                        data-testid="create-event-category"
                        onClick={() => setPickerOpen(true)}
                        className={[
                            "w-full flex items-center gap-2 h-14 px-4 rounded-2xl border bg-secondary text-left transition-colors",
                            errors.category
                                ? "border-destructive bg-destructive/5"
                                : "border-border hover:bg-muted",
                        ].join(" ")}
                    >
                        <span
                            className={[
                                "flex-1 truncate text-[15px]",
                                categoryLabel ? "" : "text-muted-foreground/80",
                            ].join(" ")}
                        >
                            {categoryLabel || "Choose a category"}
                        </span>
                        <ChevronRight size={16} className="text-muted-foreground" />
                    </button>
                    {errors.category && (
                        <p data-testid="create-event-category-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.category}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Date" error={errors.date} testId="create-event-date">
                        <input
                            data-testid="create-event-date"
                            type="date"
                            min={today}
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>
                    <Field label="Time" error={errors.time} testId="create-event-time">
                        <input
                            data-testid="create-event-time"
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>
                </div>

                <Field label="Location" testId="create-event-location">
                    <input
                        data-testid="create-event-location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                <div className="flex items-center justify-between p-3 rounded-2xl border border-border bg-secondary">
                    <div>
                        <div className="text-[13.5px] font-medium">Free event</div>
                        <div className="text-[11.5px] text-muted-foreground">Attendees join at no cost</div>
                    </div>
                    <button
                        type="button"
                        data-testid="create-event-isfree-toggle"
                        role="switch"
                        aria-checked={isFree}
                        onClick={() => setIsFree((x) => !x)}
                        className={[
                            "relative h-7 w-12 rounded-full transition-colors",
                            isFree ? "bg-primary" : "bg-muted",
                        ].join(" ")}
                    >
                        <span
                            className={[
                                "absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-soft transition-[left]",
                                isFree ? "left-[22px]" : "left-0.5",
                            ].join(" ")}
                        />
                    </button>
                </div>

                {!isFree && (
                    <Field label="Ticket price (₦)" error={errors.price} testId="create-event-price">
                        <input
                            data-testid="create-event-price"
                            type="number"
                            inputMode="numeric"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            placeholder="e.g. 5000"
                            className="flex-1 bg-transparent outline-none text-[15px]"
                        />
                    </Field>
                )}

                {!isFree && (
                    <section data-testid="create-event-refund-policy">
                        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                            Refund policy
                        </span>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { id: "refundable", label: "Refundable" },
                                { id: "non_refundable", label: "Non-refundable" },
                            ].map((opt) => {
                                const active = refundPolicy === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        data-testid={`create-event-refund-${opt.id}`}
                                        onClick={() => setRefundPolicy(opt.id)}
                                        aria-pressed={active}
                                        className={[
                                            "h-12 rounded-2xl border text-[13.5px] font-medium transition-colors",
                                            active
                                                ? "border-primary bg-primary/5 text-foreground"
                                                : "border-border bg-secondary text-muted-foreground hover:bg-muted",
                                        ].join(" ")}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="mt-2 ml-1 text-[11.5px] text-muted-foreground inline-flex items-start gap-1.5">
                            <ShieldCheck size={12} className="mt-0.5 text-primary shrink-0" />
                            <span>
                                Neyra guarantees refunds if the event is cancelled or does not take place.
                            </span>
                        </p>
                    </section>
                )}

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[12px] font-medium text-muted-foreground ml-1">
                            Description
                        </label>
                        <button
                            type="button"
                            data-testid="create-event-ai-generate"
                            onClick={handleAIGenerate}
                            disabled={aiBusy}
                            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-medium text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 disabled:opacity-60 transition-colors"
                        >
                            <Wand2 size={12} />
                            {aiBusy ? "Generating…" : "Generate with AI"}
                        </button>
                    </div>
                    <textarea
                        data-testid="create-event-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        maxLength={500}
                        placeholder="What's the event about?"
                        className={[
                            "w-full p-3.5 rounded-2xl border outline-none text-[14px] resize-none transition-colors",
                            errors.description
                                ? "border-destructive bg-destructive/5"
                                : "border-border bg-secondary focus:bg-background focus:border-primary",
                        ].join(" ")}
                    />
                    {errors.description && (
                        <p data-testid="create-event-description-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.description}
                        </p>
                    )}
                </div>
            </div>

            <div className="px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background">
                {limitMsg && (
                    <div
                        data-testid="create-event-limit-msg"
                        className="mb-2 px-3 py-2 rounded-xl bg-primary/10 border border-primary/30 text-[12px] text-primary inline-flex items-center justify-between gap-2 w-full"
                    >
                        <span>{limitMsg}</span>
                        <button
                            type="button"
                            data-testid="create-event-upgrade-btn"
                            onClick={() => navigate("/premium")}
                            className="font-semibold underline shrink-0"
                        >
                            Upgrade
                        </button>
                    </div>
                )}
                <button
                    data-testid="create-event-submit"
                    onClick={handleSubmit}
                    disabled={busy}
                    className="w-full h-14 rounded-2xl text-primary-foreground font-medium text-[15px] active:scale-[0.98] disabled:opacity-60"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    {isEdit ? "Save changes" : "Publish event"}
                </button>
                {isEdit && (
                    <p
                        data-testid="create-event-edit-hint"
                        className="mt-2 text-[11px] text-muted-foreground text-center"
                    >
                        You're editing an existing event — changes save instantly on submit.
                    </p>
                )}
            </div>

            <CategoryPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                value={cat}
                onChange={(v) => {
                    setCat(v);
                    if (errors.category) setErrors({ ...errors, category: null });
                }}
                categories={EVENT_CATEGORIES}
                title="Choose an event category"
                otherPlaceholder="Describe your event"
                otherHelp="Tell us what kind of event you're hosting."
            />
        </div>
    );
};

const Field = ({ label, error, testId, children }) => (
    <label className="block" data-testid={`${testId}-field`}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">{label}</span>
        <span
            className={[
                "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
                error
                    ? "border-destructive bg-destructive/5"
                    : "border-border bg-secondary focus-within:bg-background focus-within:border-primary",
            ].join(" ")}
        >
            {children}
        </span>
        {error && (
            <span className="block text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>
                {error}
            </span>
        )}
    </label>
);

export default CreateEvent;
